import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  ChangeDetectorRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ShareModule } from '../share.module';

import * as FileSaver from 'file-saver';
import { SearchService } from '../services/search.service';
import { SharedService } from '../services/shared.service';
import { FilterPayload } from '../../models/filter-payload.model';
import { LoadingService } from '../services/loading.service';
import { TranslateModule } from '@ngx-translate/core';
import { TableColumn } from '../../models/TableColumn.model';
import { EligibleSystem, IvkView, IvkViewType, RelatedIvkData } from '../../models/EligibleSystem.model';
import { FilterCriteriaService } from '../services/filter-criteria.service';
import { DataService } from '../services/data.service';
import { Router } from '@angular/router';
import { ResultViewService, ResultViewColumn, UserResultView, CreateUserResultViewDTO } from '../services/result-view.service';
import { UserColumn } from '../../models/user-info.model';

import { ALL_SYSTEMS, EXPANDED_PAGE_SIZE, INITIAL_ACTIVE_TAB, INITIAL_PAGE_SIZE, TABLE_CONST_COLUMNS } from '../constants/constants';
import { Notification, NotificationComponent } from '../notification/notification.component';
import { Subject, takeUntil } from 'rxjs';
import { createExcelWorkbookBlob } from '../shared/utils/excel-file.util';
import { BACKEND_MAPPING_FIELDS } from '../constants/filter-criteria.constants';

type TableMode = 'page' | 'modal';

interface VisibleColumnGroup {
  key: string;
  label: string;
  colspan: number;
}

interface SavedResultView {
  id: number;
  name: string;
  isDefault: boolean;
  columns: ResultViewColumn[];
}
@Component({
  selector: 'app-eligible-systems-table',
  standalone: true,
  imports: [ShareModule, TranslateModule, NotificationComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './eligible-systems-table.component.html',
  styleUrls: ['./eligible-systems-table.component.scss'],
})
export class EligibleSystemsTableComponent implements OnInit, OnDestroy {
  @Input() autoLoadSavedFilters: boolean = false;
  private readonly defaultTableColumns: TableColumn[] = TABLE_CONST_COLUMNS.map((column) => ({ ...column }));
  private readonly relatedSectionConfig: Array<{ type: Exclude<IvkViewType, 'system'>; prefix: string; labelPrefix: string }> = [
    { type: 'workstation', prefix: 'WS', labelPrefix: 'WS' },
    { type: 'component', prefix: 'COMP', labelPrefix: 'Comp' },
    { type: 'license', prefix: 'LIC', labelPrefix: 'Lic' },
  ];

  allSystemsOriginal: EligibleSystem[] = [];
  activeTab: string = INITIAL_ACTIVE_TAB;
  columnSearchFilters: Record<string, string> = {};
  hasTestRunBeenExecuted: boolean = false;
  showNoDataMessage: boolean = false;
  notification: Notification = {
  description: '',
  label: '',
  type: '',
};

  allTableColumns: TableColumn[] = this.defaultTableColumns.map((column) => ({ ...column }));
  generatedIvkColumns: TableColumn[] = [];
  combinedStatusFilter: string[] = [];
  showColumnVisibilityPopup: boolean = false;
  globalSearchTerm: string = '';
  selectedStatusCombinations: Array<{status: string, substatus: string}> = [];
  columnSearchTerm: string = '';
  selectedFilteredColumns: any[] = [];
  unselectedFilteredColumns: any[] = [];
  
  // --- SORTING PROPERTIES ---
  sortField: string | '' = '';
  sortOrder: 1 | -1 = 1;

  // --- PAGINATION & EXPAND PROPERTIES ---
  pageSize: any = INITIAL_PAGE_SIZE;
  pageIndex: number = 1;
  pageSizeOptions: any[] = [5, 10, 25, 50, 100, ALL_SYSTEMS];
  isTableExpanded: boolean = false;
  totalRecords: number = 0;
  isPageSizeInitialized = false;
  apiCallStartTime: number = 0;
  timerDuration: string = '00:00.00';
  isExporting: boolean = false;
  isTimerRunning: boolean = false;
  chunkedResponse: EligibleSystem[][] = [];
  filteredChunkedResponse: EligibleSystem[][] = [];
  showEligibleSystems: boolean = false;
  tableMessage: string = '';
  tableMessageType: 'error' | 'info' | '' = '' ;

  // --- SAVE RESULT VIEW PROPERTIES ---
  savedResultViews: SavedResultView[] = [];
  selectedResultViewName: string = '';
  isResultViewDirty: boolean = false;
  showResultViewMenu: boolean = false;
  showSaveResultViewModal: boolean = false;
  resultViewNameInput: string = '';
  setAsDefaultResultView: boolean = false;
  resultViewNameError: string = '';
  similarResultViewNames: string[] = [];
  isSaveAsNewMode: boolean = false;
  editingResultViewId: number | null = null;
  isSavingResultView: boolean = false;

  private destroy$ = new Subject<void>();
  private readonly apiCancel$ = new Subject<void>();
  private readonly timeoutHandle: any = null;
  private readonly API_TIMEOUT_MS = 150000;
  private readonly scrollSyncCleanup = new Map<TableMode, () => void>();
  private isSyncingProgrammaticScroll: boolean = false;
  private backendFieldToColumnMap: { [key: string]: string } = {
    'materialno': 'EQ_MAT_ID',
    'country': 'EQ_COUNTRY_ID',
    'serialno': 'EQ_SERIAL_ID',
    'version': 'EQ_SW_VS',
    'uinumber': 'EQ_ID',
    'servicePartner': 'EQ_SERVICE_PARTNER_ID',
    'businessline': 'LEVEL_BL_TEXT',
    'category': 'CONTRACT_TYPE',
    'contractstatus': 'CONTRACT_STATUS',
    'sysivk': 'HC_IVK_SYSTEM',
    'ruhready': 'EQ_RUH_FLG',
    'refurbished': 'EQ_REFURB_FLG',
    'asuenabled': 'EQ_ASU_FLG',
    'HC_DELIVERY_DATE': 'EQ_DELIVERY_DT',
    'HC_EOD': 'EQ_EOD_DT',
    'HC_EOS': 'EQ_EOS_DT',
    'cistatus': 'EQ_STATUS',
    'cisubstatus': 'EQ_SUB_STATUS'
  };

  private temporarilyVisibleColumns: Set<string> = new Set();
  private readonly manuallyHiddenQueryColumns: Set<string> = new Set();
  private temporaryColumnBaseline: TableColumn[] | null = null;
  private wasResultViewDirtyBeforeTemporaryColumns: boolean = false;

  // --- IVK VIEW SCROLLER ---
  ivkViews: IvkView[] = [{ type: 'system', ivkIndex: -1, label: 'System' }];
  activeViewIdx: number = 0;
  showViewPopover: boolean = false;
  private viewPopoverHideTimer: ReturnType<typeof setTimeout> | null = null;

  get currentView(): IvkView {
    return this.ivkViews[this.activeViewIdx] ?? this.ivkViews[0];
  }

  selectView(index: number): void {
    this.activeViewIdx = index;
    this.showViewPopover = false;
    this.scrollToIvkColumns(this.ivkViews[index] ?? this.ivkViews[0], this.isTableExpanded);
    this.cdr.detectChanges();
  }

  toggleViewPopover(): void {
    this.showViewPopover = !this.showViewPopover;
  }

  onIvkScrollerMouseEnter(): void {
    if (this.viewPopoverHideTimer) {
      clearTimeout(this.viewPopoverHideTimer);
      this.viewPopoverHideTimer = null;
    }
    this.showViewPopover = true;
  }

  onIvkScrollerMouseLeave(): void {
    if (this.viewPopoverHideTimer) {
      clearTimeout(this.viewPopoverHideTimer);
    }

    this.viewPopoverHideTimer = setTimeout(() => {
      this.showViewPopover = false;
      this.viewPopoverHideTimer = null;
    }, 150);
  }

  onActiveDotMouseEnter(index: number): void {
    if (index !== this.activeViewIdx) {
      return;
    }
    this.onIvkScrollerMouseEnter();
  }

  onActiveDotMouseLeave(index: number): void {
    if (index !== this.activeViewIdx) {
      return;
    }
    this.onIvkScrollerMouseLeave();
  }

  onDotClick(index: number, event: Event, isModal: boolean): void {
    event.stopPropagation();
    if (index === this.activeViewIdx) {
      this.toggleViewPopover();
      this.scrollToIvkColumns(this.ivkViews[index] ?? this.ivkViews[0], isModal);
      return;
    }

    this.activeViewIdx = index;
    this.showViewPopover = false;
    this.scrollToIvkColumns(this.ivkViews[index] ?? this.ivkViews[0], isModal);
    this.cdr.detectChanges();
  }

  private scrollToIvkColumns(view: IvkView, isModal: boolean): void {
    setTimeout(() => {
      const tableHost = this.getTableHostElement(isModal);
      const target = tableHost?.querySelector(this.getIvkColumnSelector(view)) as HTMLElement | null;
      const scrollContainer = this.getTableScrollContainer(isModal, target);

      if (scrollContainer && target) {
        const targetOffset = target.getBoundingClientRect().left - scrollContainer.getBoundingClientRect().left + scrollContainer.scrollLeft;
        this.isSyncingProgrammaticScroll = true;
        const targetLeft = Math.max(targetOffset - 16, 0);
        scrollContainer.scrollLeft = targetLeft;
        if (typeof scrollContainer.scrollTo === 'function') {
          scrollContainer.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
        scrollContainer.dispatchEvent(new Event('scroll'));

        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

        window.setTimeout(() => {
          this.isSyncingProgrammaticScroll = false;
          this.syncActiveViewWithScroll(isModal);
        }, 250);
      }
    }, 0);
  }

  getColumnIvkGroup(column: TableColumn): IvkViewType {
    return column.groupType ?? 'system';
  }

  getColumnIvkIndex(column: TableColumn): number | null {
    return column.groupIndex ?? null;
  }

  getSortableColumnField(column: TableColumn): string | undefined {
    return column.isSortable === false ? undefined : String(column.colName);
  }

  private computeIvkViews(): void {
    const data = this.chunkedResponse.flat();

    const maxWorkstations = data.reduce(
      (max, row) => Math.max(max, this.groupAndMergeByEqIdHq(this.getRelatedRecords(row, 'workstation')).length),
      0
    );
    const maxComponents = data.reduce(
      (max, row) => Math.max(max, this.groupAndMergeByEqIdHq(this.getRelatedRecords(row, 'component')).length),
      0
    );
    const maxLicenses = data.reduce(
      (max, row) => Math.max(max, this.groupAndMergeByEqIdHq(this.getRelatedRecords(row, 'license')).length),
      0
    );

    const views: IvkView[] = [{ type: 'system', ivkIndex: -1, label: 'System' }];

    for (let i = 0; i < maxWorkstations; i++) {
      views.push({ type: 'workstation', ivkIndex: i, label: `Workstation ${i + 1}` });
    }
    for (let i = 0; i < maxComponents; i++) {
      views.push({ type: 'component', ivkIndex: i, label: `Component ${i + 1}` });
    }
    for (let i = 0; i < maxLicenses; i++) {
      views.push({ type: 'license', ivkIndex: i, label: `License ${i + 1}` });
    }

    this.ivkViews = views;
    this.activeViewIdx = 0;
    this.scheduleScrollSync();
  }

  constructor(
    private search: SearchService,
    private sharedService: SharedService,
    public loadingService: LoadingService,
    private filterCriteriaService: FilterCriteriaService,
    private dataService: DataService,
    public router: Router, 
    private readonly resultViewService: ResultViewService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadUserResultViews();
    this.allTableColumns.forEach((col) => {
      if (col.isFilterable) {
        this.columnSearchFilters[String(col.colName)] = '';
      }
    });

    this.sharedService.activeQueryTab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tab) => {
      console.log('Query tab switched to:', tab);
      // Clear results when switching tabs
      this.allSystemsOriginal = [];
      this.totalRecords = 0;
      this.pageIndex = 1;
      this.globalSearchTerm = '';
      this.columnSearchFilters = {};
      this.allTableColumns.forEach((col) => {
        if (col.isFilterable) {
          this.columnSearchFilters[String(col.colName)] = '';
        }
      });
      
      this.clearTemporaryColumns();
      this.generatedIvkColumns = [];
      this.ivkViews = [{ type: 'system', ivkIndex: -1, label: 'System' }];
      this.activeViewIdx = 0;
      
      this.cdr.detectChanges();
    });

    this.sharedService.testRunSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe((source) => {
      if (source === 'manual') {
        this.activeTab = 'manual';
      } else if (source === 'llm') {
        this.activeTab = 'llm';
      }
      this.cdr.detectChanges(); 
    });

    // Subscribe to test run triggers
    this.sharedService.testRunTriggered$    
    .pipe(takeUntil(this.destroy$))
    .subscribe((triggered) => {
      if (triggered) {
        this.apiCancel$.next();
        this.isTimerRunning = false;
        this.totalRecords = 0;
        this.pageIndex = 1;
        this.hasTestRunBeenExecuted = true;
        this.showNoDataMessage = false;
        
        this.fetchAllSystemsFromAPI();
      } else {
        // Reset table when test run is reset
        this.hasTestRunBeenExecuted = false;
        this.showNoDataMessage = false;
        this.allSystemsOriginal = [];
        this.totalRecords = 0;
        this.pageIndex = 1;
        this.globalSearchTerm = '';
        
        // Clear column filters
        this.columnSearchFilters = {};
        this.allTableColumns.forEach((col) => {
          if (col.isFilterable) {
            this.columnSearchFilters[String(col.colName)] = '';
          }
        });
        
        this.clearTemporaryColumns();
        this.generatedIvkColumns = [];
        this.ivkViews = [{ type: 'system', ivkIndex: -1, label: 'System' }];
        this.activeViewIdx = 0;
      }
    });

    // Auto-load saved filters if enabled
    if (this.autoLoadSavedFilters) {
      setTimeout(() => {
        this.loadSavedFiltersAndExecute();
      }, 100);
    }
  }

  private loadSavedFiltersAndExecute(): void {
    const guid = this.dataService.selectedGUID;
    
    if (!guid) {
      return;
    }

    
    // Set loading state
    this.loadingService.setLoading('eligibleSystemsTable', true);
    
    this.filterCriteriaService.getFilterCriteria(guid).subscribe({
      next: (savedData) => {
        if (savedData && savedData.filter) {
          try {
            const parsedRootGroup = JSON.parse(savedData.filter);
            const filterPayload: FilterPayload = { rootGroup: parsedRootGroup };
            
            
            if (this.isFilterEmpty(filterPayload)) {
              this.hasTestRunBeenExecuted = false;
              this.showNoDataMessage = true;
              this.allSystemsOriginal = [];
              this.totalRecords = 0;
              this.loadingService.setLoading('eligibleSystemsTable', false);
              return;
            }
            
            this.hasTestRunBeenExecuted = true;
            this.showNoDataMessage = false; 
            
            this.sharedService.updateTableData(filterPayload);
            
            this.fetchAllSystemsFromAPI(filterPayload);
            
          } catch (error) {
            console.error('Error parsing saved filter criteria:', error);
            this.loadingService.setLoading('eligibleSystemsTable', false);
          }
        } else {
          this.hasTestRunBeenExecuted = false;
          this.showNoDataMessage = true;
          this.allSystemsOriginal = [];
          this.totalRecords = 0;
          this.loadingService.setLoading('eligibleSystemsTable', false);
        }
      },
      error: (error) => {
        console.error('Error loading saved filter criteria:', error);
        this.loadingService.setLoading('eligibleSystemsTable', false);
      }
    });
  }

  private processPayloadForHiddenColumns(payload: FilterPayload | null = this.sharedService.latestPayload): void {
    this.clearTemporaryColumns();
    if (!payload || !payload.rootGroup) {
      return;
    }

    const fieldsInPayload = this.extractFieldsFromPayload(payload.rootGroup);
    
    fieldsInPayload.forEach(field => {
      const normalizedField = field.toLowerCase();
      const columnName = Object.entries(this.backendFieldToColumnMap)
        .find(([key]) => key.toLowerCase() === normalizedField)?.[1];
      const fieldLabel = Object.entries(BACKEND_MAPPING_FIELDS)
        .find(([key]) => key.toLowerCase() === normalizedField)?.[1] ?? field;
      const column = this.allTableColumns.find(col => col.colName === columnName)
        ?? this.allTableColumns.find(col => String(col.colName).toLowerCase() === normalizedField)
        ?? this.allTableColumns.find(col => col.displayName.toLowerCase() === fieldLabel.toLowerCase());
      if (column && !column.visible && !this.manuallyHiddenQueryColumns.has(String(column.colName))) {
        this.makeColumnTemporarilyVisible(column);
      }
    });
  }

  private extractFieldsFromPayload(group: any): string[] {
    const fields: string[] = [];
    
    if (!group || !group.conditions) {
      return fields;
    }
    
    for (const condition of group.conditions) {
      if (condition.type === 'Filter' && condition.field) {
        fields.push(condition.field);
      } else if (condition.type === 'Group') {
        fields.push(...this.extractFieldsFromPayload(condition));
      }
    }
    
    return fields;
  }

  private makeColumnTemporarilyVisible(column: TableColumn): void {
    if (this.temporarilyVisibleColumns.size === 0) {
      this.temporaryColumnBaseline = this.allTableColumns.map(existingColumn => ({ ...existingColumn }));
      this.wasResultViewDirtyBeforeTemporaryColumns = this.isResultViewDirty;
    }
    this.temporarilyVisibleColumns.add(String(column.colName));
    
    const currentIndex = this.allTableColumns.findIndex(col => col.colName === column.colName);
    if (currentIndex > -1) {
      this.allTableColumns.splice(currentIndex, 1);
    }
    
    const firstVisibleIndex = this.allTableColumns.findIndex(col => col.visible);
    
    const insertIndex = firstVisibleIndex > -1 ? firstVisibleIndex : 0;
    
    const tempColumn = { ...column, visible: true };
    this.allTableColumns.splice(insertIndex, 0, tempColumn);

    this.markResultViewDirty(true);
    this.refreshColumnLists();
    this.cdr.detectChanges();
  }

  private clearTemporaryColumns(): void {
    if (this.temporarilyVisibleColumns.size === 0) {
      return;
    }

    const temporaryColumns = this.temporarilyVisibleColumns;
    const restoredColumns = this.allTableColumns.filter(column => !temporaryColumns.has(String(column.colName)));
    this.temporaryColumnBaseline?.forEach((column, index) => {
      if (temporaryColumns.has(String(column.colName))) {
        restoredColumns.splice(index, 0, { ...column });
      }
    });
    this.allTableColumns = restoredColumns;
    this.isResultViewDirty = this.wasResultViewDirtyBeforeTemporaryColumns;
    this.temporarilyVisibleColumns.clear();
    this.temporaryColumnBaseline = null;
    this.refreshColumnLists();
  }

  get visibleTableColumns(): TableColumn[] {
    return [...this.allTableColumns, ...this.generatedIvkColumns].filter((col) => col.visible);
  }

  get visibleColumnGroups(): VisibleColumnGroup[] {
    const groups: VisibleColumnGroup[] = [];

    for (const column of this.visibleTableColumns) {
      const key = this.getColumnGroupKey(column);
      const label = this.getColumnGroupLabel(column);
      const lastGroup = groups[groups.length - 1];

      if (lastGroup && lastGroup.key === key) {
        lastGroup.colspan += 1;
        continue;
      }

      groups.push({ key, label, colspan: 1 });
    }

    return groups;
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  exportToExcel(filter?: FilterPayload): void {
    if (this.totalRecords === 0) {
      this.notification = { description: '', label: '', type: '' };
      setTimeout(() => {
        this.notification = {
          label: 'Error',
          description: 'No data available to export.',
          type: 'error',
        };
      }, 10);
      return;
    
    }

    this.isExporting = true;
    const filterToUse = filter ?? this.sharedService.latestPayload ?? { rootGroup: { type: 'Group', operator: 'AND', conditions: [] } };

    this.search.searchAllUpdateMachines(filterToUse).subscribe({
      next: (data) => {
        const allSystemsData = data.items || [];
        const summaryRows = this.buildQuerySummaryRows(filterToUse);
        const normalizedExportRows = this.normalizeIvkData(allSystemsData);
        const visibleColumns = this.visibleTableColumns;
        const resultRows = normalizedExportRows.length > 0
          ? [
              visibleColumns.map((column) => column.displayName),
              ...normalizedExportRows.map((system) =>
                visibleColumns.map((column) => system[column.colName] ?? ''),
              ),
            ]
          : [];

        const fileName = this.buildExportFileName();

        createExcelWorkbookBlob([
          {
            name: 'Query Summary',
            rows: summaryRows,
            columnWidths: [20, 20, 30, 20],
          },
          {
            name: 'Result Set',
            rows: resultRows,
          },
        ]).then((workbookBlob) => {
          FileSaver.saveAs(workbookBlob, fileName);
          this.isExporting = false;
        }).catch((err) => {
          this.handleExportError(err);
        });
      },
      error: (err) => {
        this.handleExportError(err);
      },
    });
  }

  private handleExportError(err: unknown): void {
    console.error('Export error:', err);
    this.isExporting = false;
    this.notification = { description: '', label: '', type: '' };
    setTimeout(() => {
      this.notification = {
        label: 'Error',
        description: 'Failed to export data. Please try again.',
        type: 'error',
      };
    }, 10);
  }


  onGlobalSearchChange(): void {
    this.pageIndex = 1;
    this.markResultViewDirty();
  }

  onSort(event: any): void {
    this.sortField = event.field;
    this.sortOrder = event.order;
    this.pageIndex = 1;
    this.markResultViewDirty();
  }

  get filteredData(): EligibleSystem[] {
    if (!this.chunkedResponse || this.chunkedResponse.flat().length === 0) return [];
    let data = [...this.chunkedResponse.flat()];
  
    if (this.selectedStatusCombinations.length > 0) {
      data = data.filter(item => {
        return this.selectedStatusCombinations.some(combination => {
          const itemStatus = item['EQ_SUB_STATUS']?.toLowerCase();
          const itemSubstatus = item['EQ_STATUS']?.toLowerCase();
          
          return itemStatus === combination.status.toLowerCase() && 
                 itemSubstatus === combination.substatus.toLowerCase();
        });
      });
    }
  
    const activeFilterKeys = Object.keys(this.columnSearchFilters)
      .filter(key => {
        if (this.selectedStatusCombinations.length > 0 && (key === 'EQ_SUB_STATUS' || key === 'EQ_STATUS')) {
          return false;
        }
        return this.columnSearchFilters[key] && this.columnSearchFilters[key]!.trim() !== '';
      });
  
    activeFilterKeys.forEach((key) => {
      const filterValue = this.columnSearchFilters[key]!.trim().toLowerCase();
      const columnDef = this.allTableColumns.find(c => c.colName === key);
      data = data.filter((systemRow) => {
        const rowValue = systemRow[key];
        if (rowValue === null || rowValue === undefined) return false;
        const rowValueLower = String(rowValue).toLowerCase();
        if (columnDef && (columnDef.filterType === 'select' || columnDef.filterType === 'booleanSelect')) {
          return rowValueLower === filterValue;
        }
        else {
          return rowValueLower.includes(filterValue);
        }
      });
    });
  
    if (this.globalSearchTerm.trim() !== '') {
      const searchTermLower = this.globalSearchTerm.trim().toLowerCase();
      data = data.filter(systemRow =>
        Object.values(systemRow).some(value =>
          value !== null && value !== undefined && String(value).toLowerCase().includes(searchTermLower)
        )
      );
    }

    // Apply sorting
    if (this.sortField) {
      const field = this.sortField;
      const order = this.sortOrder;
      data.sort((a, b) => {
        const valA = a[field];
        const valB = b[field];
        if (valA == null && valB == null) return 0;
        if (valA == null) return order;
        if (valB == null) return -order;
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        if (strA < strB) return -1 * order;
        if (strA > strB) return 1 * order;
        return 0;
      });
    }
   this.filteredChunkedResponse = this.isShowAllSystems() ? [data] : this.chunkAndInitializeData(data);
    this.totalRecords = data.length;
    this.getTotalPages();

    if (this.isShowAllSystems()) {
      return this.filteredChunkedResponse[0] ?? [];
    }
   return this.filteredChunkedResponse[this.pageIndex] ?? [];
  }

  refreshColumnLists(): void {
    const term = this.columnSearchTerm?.toLowerCase() || '';
    const filtered = this.allTableColumns.filter(c =>
      c.displayName.toLowerCase().includes(term)
    );
    this.selectedFilteredColumns = filtered
      .filter(c => c.visible)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.unselectedFilteredColumns = filtered
      .filter(c => !c.visible)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  onColumnSearch(term: string): void {
    this.columnSearchTerm = term;
    this.refreshColumnLists();
  }

  toggleColumnVisibilityPopup(): void {
    this.showColumnVisibilityPopup = !this.showColumnVisibilityPopup;
    if (this.showColumnVisibilityPopup) {
      this.columnSearchTerm = '';
      this.refreshColumnLists();
      setTimeout(() => {
        const list = document.querySelector('.column-visibility-list') as HTMLElement;
        if (list) {
          list.scrollTop = 0;
        }
      }, 50);
    }
  }

  onColumnVisibilityChange(columnToToggle: TableColumn, event: Event): void {
    // sh-checkbox exposes its state via `active` (not `checked`); fall back to
    // the native input `checked` and finally to the column's own value.
    const target = event.target as (HTMLElement & { active?: boolean; checked?: boolean }) | null;
    const isChecked = target?.active ?? target?.checked ?? columnToToggle.visible ?? false;
    const columnIndex = this.allTableColumns.findIndex(col => col.colName === columnToToggle.colName);
    
    if (columnIndex > -1) {
      this.allTableColumns[columnIndex].visible = isChecked;
      if (isChecked) {
        this.manuallyHiddenQueryColumns.delete(String(columnToToggle.colName));
      } else {
        this.manuallyHiddenQueryColumns.add(String(columnToToggle.colName));
      }
      
      if (this.temporarilyVisibleColumns.has(String(columnToToggle.colName))) {
        this.temporarilyVisibleColumns.delete(String(columnToToggle.colName));
      }
      this.refreshColumnLists();
      this.pageIndex = 1;
      this.markResultViewDirty();
    }
  }

  // --- SAVE RESULT VIEW METHODS ---
  get displayedResultViewName(): string {
    const base = this.selectedResultViewName || 'Untitled result view';
    return this.isResultViewDirty && this.selectedResultViewName ? `${base}*` : base;
  }

  get isSelectedViewDefault(): boolean {
    const view = this.savedResultViews.find(v => v.name === this.selectedResultViewName);
    return !!view?.isDefault;
  }

  get isDuplicateResultViewName(): boolean {
    const name = this.resultViewNameInput.trim().toLowerCase();
    if (!name) {
      return false;
    }
    return this.savedResultViews.some(v => v.name.toLowerCase() === name && v.id !== this.editingResultViewId);
  }

  get isSaveViewDisabled(): boolean {
    // Nothing to save when a saved view is selected and it has no unsaved changes.
    return this.isSavingResultView || (!!this.selectedResultViewName && !this.isResultViewDirty);
  }

  markResultViewDirty(isTemporaryChange: boolean = false): void {
    if (this.selectedResultViewName) {
      this.isResultViewDirty = true;
      if (!isTemporaryChange) {
        this.wasResultViewDirtyBeforeTemporaryColumns = true;
      }
    }
  }

  onResultViewDropdownChange(event: any): void {
    const value = event?.target?.value ?? '';
    const matchedView = this.savedResultViews.find(v => v.name === value);
    if (matchedView) {
      this.selectResultView(matchedView);
    }
  }

  selectResultView(view: SavedResultView): void {
    this.clearTemporaryColumns();
    if (this.selectedResultViewName !== view.name) {
      this.manuallyHiddenQueryColumns.clear();
    }
    this.selectedResultViewName = view.name;
    this.isResultViewDirty = false;
    if (view.columns && view.columns.length > 0) {
      this.applyResultViewColumns(view.columns);
      this.processPayloadForHiddenColumns();
      this.refreshColumnLists();
    }
  }

  toggleResultViewMenu(): void {
    this.showResultViewMenu = !this.showResultViewMenu;
  }

  @HostListener('document:click', ['$event'])
  closeResultViewMenuOnOutsideClick(event: MouseEvent): void {
    if (!this.showResultViewMenu) {
      return;
    }

    const target = event.target as Element | null;
    if (!target?.closest('.result-view-select-wrapper')) {
      this.showResultViewMenu = false;
    }
  }

  openSaveResultViewModal(): void {
    if (this.isSaveViewDisabled) {
      return;
    }

    const selectedView = this.getSelectedResultView();
    if (selectedView) {
      this.saveSelectedResultViewChanges(selectedView);
      return;
    }

    this.isSaveAsNewMode = true;
    this.editingResultViewId = null;
    this.resultViewNameInput = '';
    this.setAsDefaultResultView = false;
    this.resultViewNameError = '';
    this.similarResultViewNames = [];
    this.showResultViewMenu = false;
    this.showSaveResultViewModal = true;
  }

  openSaveAsNewResultViewModal(): void {
    this.isSaveAsNewMode = true;
    this.editingResultViewId = null;
    this.resultViewNameInput = '';
    this.setAsDefaultResultView = false;
    this.resultViewNameError = '';
    this.similarResultViewNames = [];
    this.showResultViewMenu = false;
    this.showSaveResultViewModal = true;
  }

  openRenameResultViewModal(): void {
    this.isSaveAsNewMode = false;
    this.editingResultViewId = this.getSelectedResultView()?.id ?? null;
    this.resultViewNameInput = this.selectedResultViewName;
    this.setAsDefaultResultView = this.isSelectedViewDefault;
    this.resultViewNameError = '';
    this.similarResultViewNames = [];
    this.showResultViewMenu = false;
    this.showSaveResultViewModal = true;
  }

  closeSaveResultViewModal(): void {
    this.showSaveResultViewModal = false;
    this.resultViewNameError = '';
    this.similarResultViewNames = [];
    this.editingResultViewId = null;
  }

  onResultViewNameInput(valueOrEvent: string | Event | { target?: { value?: string } }): void {
    const eventTarget = (valueOrEvent as { target?: { value?: string } }).target;
    const value = typeof valueOrEvent === 'string'
      ? valueOrEvent
      : (eventTarget?.value ?? '');
    this.resultViewNameInput = value;
    this.resultViewNameError = '';
    const term = (value || '').trim().toLowerCase();
    if (!term) {
      this.similarResultViewNames = [];
      return;
    }
    this.similarResultViewNames = this.savedResultViews
      .map(v => v.name)
      .filter(name => name.toLowerCase().includes(term) && name.toLowerCase() !== term)
      .slice(0, 3);
  }

  saveOrOverwriteResultView(): void {
    const name = this.resultViewNameInput.trim();
    if (!name) {
      this.resultViewNameError = 'View name cannot be empty.';
      return;
    }

    const columns = this.buildCurrentColumnConfig();
    const isDefault = this.setAsDefaultResultView;
    const payload: CreateUserResultViewDTO = { viewName: name, isDefault, columns };

    // Decide whether to overwrite (PUT) an existing view or create (POST) a new one.
    const existingMatch = this.savedResultViews.find(v => v.name.toLowerCase() === name.toLowerCase());
    let targetId: number | null;
    if (existingMatch) {
      targetId = existingMatch.id;
    } else if (this.editingResultViewId != null && !this.isSaveAsNewMode) {
      targetId = this.editingResultViewId;
    } else {
      targetId = null;
    }

    this.isSavingResultView = true;

    if (targetId === null) {
      this.resultViewService.createResultView(payload).subscribe({
        next: () => this.onResultViewSaved(name),
        error: (error) => this.onResultViewSaveError(error, 'saving'),
      });
    } else {
      this.resultViewService.updateResultView(targetId, payload).subscribe({
        next: () => this.onResultViewSaved(name),
        error: (error) => this.onResultViewSaveError(error, 'saving'),
      });
    }
  }

  deleteResultView(): void {
    const view = this.getSelectedResultView();
    if (!view) {
      return;
    }
    const name = view.name;
    this.showResultViewMenu = false;
    this.resultViewService.deleteResultView(view.id).subscribe({
      next: () => {
        this.selectedResultViewName = '';
        this.isResultViewDirty = false;
        this.loadUserResultViews();
        this.showResultViewNotification('Deleted', `${name} result view has been deleted.`, 'success');
      },
      error: (error) => this.onResultViewSaveError(error, 'deleting'),
    });
  }

  setResultViewAsDefault(): void {
    const view = this.getSelectedResultView();
    if (!view || view.isDefault) {
      return;
    }
    this.showResultViewMenu = false;
    const payload = { viewName: view.name, isDefault: true, columns: view.columns };
    this.resultViewService.updateResultView(view.id, payload).subscribe({
      next: () => {
        this.loadUserResultViews();
        this.showResultViewNotification('Saved', `${view.name} has been set as the default view.`, 'success');
      },
      error: (error) => this.onResultViewSaveError(error, 'saving'),
    });
  }

  // --- SAVE RESULT VIEW HELPERS ---
  private getSelectedResultView(): SavedResultView | undefined {
    return this.savedResultViews.find(v => v.name === this.selectedResultViewName);
  }

  private saveSelectedResultViewChanges(selectedView: SavedResultView): void {
    const payload = {
      viewName: selectedView.name,
      isDefault: selectedView.isDefault,
      columns: this.buildCurrentColumnConfig(),
    };

    this.isSavingResultView = true;
    this.showResultViewMenu = false;
    this.resultViewService.updateResultView(selectedView.id, payload).subscribe({
      next: () => this.onResultViewSaved(selectedView.name),
      error: (error) => this.onResultViewSaveError(error, 'saving'),
    });
  }

  private buildCurrentColumnConfig(): ResultViewColumn[] {
    return this.allTableColumns.map((col, index) => ({
      name: col.colName as string,
      order: index + 1,
      visible: col.visible,
    }));
  }

  private applyResultViewColumns(columns: ResultViewColumn[]): void {
    const savedColumns: UserColumn[] = columns.map(c => ({
      name: c.name,
      order: c.order,
      visible: c.visible,
    }));
    this.applySavedColumnOrder(savedColumns);
  }

  private mapResultViews(views: UserResultView[]): void {
    this.savedResultViews = (views || [])
      .map(v => ({
        id: v.id,
        name: v.viewName,
        isDefault: v.isDefault,
        columns: v.columns || [],
      }))
      .sort((firstView, secondView) =>
        firstView.name.localeCompare(secondView.name, undefined, { sensitivity: 'base' }),
      );
  }

  private loadUserResultViews(): void {
    this.resultViewService.getAllResultViews().subscribe({
      next: (views) => {
        this.mapResultViews(views);
        const defaultView = this.savedResultViews.find(v => v.isDefault);
        if (defaultView && !this.selectedResultViewName) {
          this.selectResultView(defaultView);
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading result views:', error);
      },
    });
  }

  private onResultViewSaved(name: string): void {
    this.resultViewService.getAllResultViews().subscribe({
      next: (views) => {
        this.mapResultViews(views);
        const saved = this.savedResultViews.find(v => v.name.toLowerCase() === name.toLowerCase());
        this.selectedResultViewName = saved ? saved.name : name;
        this.isResultViewDirty = false;
        this.temporarilyVisibleColumns.clear();
        this.temporaryColumnBaseline = null;
        this.isSavingResultView = false;
        this.showSaveResultViewModal = false;
        this.editingResultViewId = null;
        this.showResultViewNotification('Saved', `${name} result view is successfully saved.`, 'success');
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSavingResultView = false;
        this.showSaveResultViewModal = false;
        this.showResultViewNotification('Saved', `${name} result view is successfully saved.`, 'success');
      },
    });
  }

  private onResultViewSaveError(error: any, action: string): void {
    console.error(`Error ${action} result view:`, error);
    this.isSavingResultView = false;
    this.showResultViewNotification('Error', `An error occurred while ${action} the result view.`, 'error');
  }

  private showResultViewNotification(label: string, description: string, type: string): void {
    this.notification = { description: '', label: '', type: '' };
    setTimeout(() => {
      this.notification = { label, description, type };
    }, 10);
  }

  getDisplayedRecordsCount(): number {
    return Math.min(this.pageIndex * this.pageSize, this.totalRecords);
  }

  getDisplayedRecordsCountBegin(): number {
    return (this.pageIndex - 1) * this.pageSize + 1;
  }

  toggleTableExpand(): void {
    if (!this.hasTestRunBeenExecuted) {
      return;
    }
    
    this.isTableExpanded = !this.isTableExpanded;
    this.pageSize = this.isTableExpanded ? EXPANDED_PAGE_SIZE : INITIAL_PAGE_SIZE;
    this.pageIndex = 1;
    this.scheduleScrollSync();
  }

  closeExpandedTable(): void {
    if (!this.isTableExpanded) {
      return;
    }
    this.isTableExpanded = false;
    this.pageSize = INITIAL_PAGE_SIZE;
    this.pageIndex = 1;
    this.scheduleScrollSync();
  }

  private isFilterEmpty(filter: FilterPayload): boolean {
    if (!filter || !filter.rootGroup) {
      return true;
    }

    return this.isGroupEmpty(filter.rootGroup);
  }

  private isGroupEmpty(group: any): boolean {
    if (!group || !group.conditions || !Array.isArray(group.conditions)) {
      return true;
    }

    if (group.conditions.length === 0) {
      return true;
    }

    for (const condition of group.conditions) {
      if (condition.type === 'Filter') {
        if (condition.field && condition.operator && condition.value !== undefined && condition.value !== null && condition.value !== '') {
          return false;
        }
      } else if (condition.type === 'Group') {
        if (!this.isGroupEmpty(condition)) {
          return false;
        }
      }
    }

    return true;
  }

  loadPreviousPage(event: any) {
  this.pageIndex = event.target.currentPage;
   this.allSystemsOriginal = this.checkFiltering(this.pageIndex);
  }

  private checkFiltering(selectedPage: number) {
    return (
      this.filteredChunkedResponse[selectedPage] ||
      this.chunkedResponse[selectedPage]
    );
  }

  loadNextPage(event: any) {
    this.pageIndex = event.target.currentPage;
    this.allSystemsOriginal = this.checkFiltering(this.pageIndex);
  }

  loadSelectedPage(event: any) {
    this.pageIndex = event.target.currentPage;
    this.allSystemsOriginal = this.checkFiltering(this.pageIndex);
  }

  getTotalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  pagePerRow(event: any): void {
    if (this.pageSize !== event.target.value) {
      if (event.target.value === ALL_SYSTEMS) {
        this.pageSize = ALL_SYSTEMS;
        this.allSystemsOriginal = this.chunkedResponse.flat();
      } else {
        this.pageSize = event.target.value;
        this.chunkedResponse = this.chunkAndInitializeData(this.chunkedResponse.flat());
        this.pageIndex = 1;
        this.allSystemsOriginal = this.chunkedResponse[this.pageIndex] ?? [];
      }
    }
  }

  isScrollableNeeded(): boolean {
    const numberOfRows = this.filteredData?.length || 0;
    return numberOfRows >= 20;
  }

  getTableScrollHeight(): string {
    const rowHeight = 40; 
    const headerHeight = 60; 
    const scrollThreshold = 20; 
    
    const numberOfRows = this.filteredData?.length || 0;
    
    if (numberOfRows < scrollThreshold) {
      const calculatedHeight = (numberOfRows * rowHeight) + headerHeight;
      const minHeight = 200; 
      return `${Math.max(calculatedHeight, minHeight)}px`;
    } else {
      const maxVisibleRows = scrollThreshold;
      const scrollHeight = (maxVisibleRows * rowHeight) + headerHeight;
      return `${scrollHeight}px`;
    }
  }

  initiateTimer() {
    if (!this.isTimerRunning) {
      return;
    }
    
    const elapsed = performance.now() - this.apiCallStartTime;
    this.timerDuration = this.formatAPIDuration(elapsed);
    if (elapsed >= this.API_TIMEOUT_MS) {
      this.handleApiTimeout();
      return;
    }

    requestAnimationFrame(() => this.initiateTimer());

  }
   private handleApiTimeout(): void {
    this.isTimerRunning = false;
    this.showEligibleSystems = true;
    this.chunkedResponse = [];
    this.filteredChunkedResponse = [];
    this.allSystemsOriginal = [];
    this.totalRecords = 0;
    this.tableMessage = 'Request timed out due to a large result set or connectivity issue. Please refine your search criteria to target a maximum of 100k records and try again.';
    this.tableMessageType = 'error';
    this.loadingService.setLoading('eligibleSystemsTable', false);
    this.cdr.detectChanges();
  }

  formatAPIDuration(differenceOfDuration: number): string {
      const minutes = Math.floor(differenceOfDuration / 60000);
    const seconds = Math.floor((differenceOfDuration % 60000) / 1000);
    const milliseconds = Math.floor(differenceOfDuration % 1000); 
    return `${this.pad(minutes)}:${this.pad(seconds)}.${this.pad(milliseconds)}`;
  }

  pad(value: number, length: number = 2): string {
    return value.toString().padStart(length, '0');
  }
  onColumnReorder(event: any) {
    const visibleColumns = this.allTableColumns.filter((col) => col.visible);

    if (event.dragIndex >= visibleColumns.length || event.dropIndex >= visibleColumns.length) {
      return;
    }
    
    const reorderedVisibleColumns = [...visibleColumns];
    const movedColumn = reorderedVisibleColumns.splice(event.dragIndex, 1)[0];
    reorderedVisibleColumns.splice(event.dropIndex, 0, movedColumn);
    
    const hiddenColumns = this.allTableColumns.filter(col => !col.visible);
    
    this.allTableColumns = [...reorderedVisibleColumns, ...hiddenColumns];
    
    this.markResultViewDirty();
  }

  private applySavedColumnOrder(savedColumns: UserColumn[]): void {
    try {
      const sortedSavedColumns = savedColumns.sort((a, b) => a.order - b.order);
      
      const orderedColumns: TableColumn[] = [];
      
      sortedSavedColumns.forEach(savedCol => {
        const existingColumn = this.allTableColumns.find(col => col.colName === savedCol.name);
        if (existingColumn) {
          orderedColumns.push({
            ...existingColumn,
            visible: savedCol.visible
          });
        }
      });
      
      this.allTableColumns.forEach(col => {
        if (!orderedColumns.find(orderedCol => orderedCol.colName === col.colName)) {
          orderedColumns.push(col);
        }
      });
      
      this.allTableColumns = orderedColumns;
      
    } catch (error) {
      console.error('Error applying saved column order:', error);
    }
  }

   private buildExportFileName(): string {
    const filterName = this.dataService.selectedFilterName?.trim() || 'IBSE';
    const now = new Date();
    const pad = (n: number, len = 2) => n.toString().padStart(len, '0');
    const datePart =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const timePart =
      `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
     const safeName = filterName
       .replaceAll('\\', '_')
       .replaceAll('/', '_')
       .replaceAll(':', '_')
       .replaceAll('*', '_')
       .replaceAll('?', '_')
       .replaceAll('"', '_')
       .replaceAll('<', '_')
       .replaceAll('>', '_')
       .replaceAll('|', '_');
    return `${safeName}_${datePart}_${timePart}.xlsx`;
  }

   private buildQuerySummaryRows(filter: FilterPayload): any[][] {


    const rows: any[][] = [];

    rows.push(['#', 'Group Operator', 'Field', 'Operator', 'Value']);

    const conditions = this.flattenConditions(filter?.rootGroup, null, true);
    if (conditions.length === 0) {
      rows.push(['–', '', 'No filters applied', '', '']);
    } else {
      let idx = 0;
      for (const c of conditions) {
        rows.push([idx + 1, c.groupOperator, c.field, c.operator, c.value]);
        idx++;
      }
    }

    return rows;
  }

  private flattenConditions(
    group: any,
    parentOperator: string | null,
    treatDirectChildGroupsAsSeparateBlocks: boolean = false
  ): Array<{ field: string; operator: string; value: string; groupOperator: string }> {
    const result: Array<{ field: string; operator: string; value: string; groupOperator: string }> = [];

    if (!group || !Array.isArray(group.conditions)) {
      return result;
    }

    const groupOp = group.operator ?? parentOperator ?? '';
    const conditions = this.expandStructuralConditions(group.conditions, groupOp);
    let hasWrittenConditionInThisGroup = false;

    for (const condition of conditions) {
      if (condition.type === 'Filter') {
        result.push(this.buildQuerySummaryCondition(condition, groupOp, hasWrittenConditionInThisGroup));
        hasWrittenConditionInThisGroup = true;
      } else if (condition.type === 'Group') {
        const nestedConditions = this.flattenConditions(condition, condition.operator ?? groupOp, false);
        if (nestedConditions.length > 0) {
          if (!treatDirectChildGroupsAsSeparateBlocks && hasWrittenConditionInThisGroup) {
            nestedConditions[0].groupOperator = '';
          }
          result.push(...nestedConditions);
          hasWrittenConditionInThisGroup = true;
        }
      }
    }

    return result;
  }

  private buildQuerySummaryCondition(
    condition: any,
    groupOperator: string,
    hasWrittenConditionInThisGroup: boolean
  ): { field: string; operator: string; value: string; groupOperator: string } {
    return {
      field: condition.field ?? '',
      operator: condition.operator ?? '',
      value: Array.isArray(condition.value)
        ? condition.value.join(', ')
        : String(condition.value ?? ''),
      groupOperator: hasWrittenConditionInThisGroup ? '' : groupOperator,
    };
  }

  private expandStructuralConditions(conditions: any[], groupOperator: string): any[] {
    return conditions.flatMap((condition) =>
      this.isStructuralChainGroup(condition, groupOperator)
        ? this.expandStructuralConditions(condition.conditions, groupOperator)
        : [condition],
    );
  }

  private isStructuralChainGroup(condition: any, groupOperator: string): boolean {
    if (!condition || condition.type !== 'Group' || !Array.isArray(condition.conditions)) {
      return false;
    }

    const conditionOperator = condition.operator ?? groupOperator ?? '';
    return conditionOperator === groupOperator && !condition.name && !condition.groupId;
  }

  // Toggle mock augmentation only for local fallback scenarios.
  private readonly MOCK_IVK_ENABLED = false;

  private normalizeIvkData(items: EligibleSystem[]): EligibleSystem[] {
    const joinValues = (records: any[], key: string): string | null => {
      const values = records
        .map((record) => record?.[key])
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => String(value).trim());

      return values.length > 0 ? values.join(', ') : null;
    };

    this.generatedIvkColumns = this.buildGeneratedIvkColumns(items);
    this.initializeDynamicColumnFilters();

    return items.map((row) => {
      const workstations = this.getRelatedRecords(row, 'workstation');
      const components = this.getRelatedRecords(row, 'component');
      const licenses = this.getRelatedRecords(row, 'license');
      const flattenedRow: EligibleSystem = {
        ...row,
        workstations,
        components,
        licenses,
        workstation: workstations[0],
        component: components[0],
        license: licenses[0],
        WS_EQ_IDS: joinValues(workstations, 'EQ_ID'),
        WS_MAT_IDS: joinValues(workstations, 'EQ_MAT_ID'),
        WS_SERIAL_IDS: joinValues(workstations, 'EQ_SERIAL_ID'),
        CMP_EQ_IDS: joinValues(components, 'EQ_ID'),
        CMP_MAT_IDS: joinValues(components, 'EQ_MAT_ID'),
        CMP_SERIAL_IDS: joinValues(components, 'EQ_SERIAL_ID'),
        LIC_EQ_IDS: joinValues(licenses, 'EQ_ID'),
        LIC_MAT_IDS: joinValues(licenses, 'EQ_MAT_ID'),
        LIC_SERIAL_IDS: joinValues(licenses, 'EQ_SERIAL_ID'),
      };

      this.flattenRelatedSection(flattenedRow, this.groupAndMergeByEqIdHq(workstations), 'workstation');
      this.flattenRelatedSection(flattenedRow, this.groupAndMergeByEqIdHq(components), 'component');
      this.flattenRelatedSection(flattenedRow, this.groupAndMergeByEqIdHq(licenses), 'license');

      return flattenedRow;
    });
  }

  private applyMockIvkData(items: EligibleSystem[]): EligibleSystem[] {
    if (!this.MOCK_IVK_ENABLED) return items;

    return items.map((sys, i) => {
      const workstationCount = (i % 2) + 1;
      const componentCount = (i % 3 === 0) ? 2 : 1;
      const licenseCount = 2;

      const workstations = Array.from({ length: workstationCount }, (_, wsIdx) => ({
        EQ_ID_HQ: `WS${wsIdx + 1}-HQ-${sys.EQ_ID_HQ ?? i}`,
        EQ_ID_RU: `WS${wsIdx + 1}-RU-${sys.EQ_ID_RU ?? i}`,
        EQ_ID: `WS${wsIdx + 1}-CI-${sys.EQ_ID ?? i}`,
        EQ_MAT_ID: `MAT-WS-${i + 1}-${wsIdx + 1}`,
        EQ_SERIAL_ID: `SN-WS-${i + 1}-${wsIdx + 1}`,
        EQ_SW_VS: `WS-SW-v${(wsIdx % 3) + 1}.0`,
        EQ_STATUS: wsIdx % 2 === 0 ? 'Active' : 'Inactive',
        EQ_SUB_STATUS: wsIdx % 2 === 0 ? 'Active' : 'Shipped',
        MAT_IVK_NAME: `Workstation ${wsIdx + 1} IVK`,
        REL_SYS_MAT_IVK_NAME: `Workstation ${wsIdx + 1} IVK`,
        EQ_TYPE: 'Workstation',
        EQ_SUBTYPE: `WS-Type-${(wsIdx % 2) + 1}`,
        EQ_RUH_FLG: wsIdx % 2 === 0 ? 'true' : 'false',
        EQ_ASU_FLG: wsIdx % 2 === 0 ? 'Y' : 'N',
        LEVEL_BL_TEXT: sys.LEVEL_BL_TEXT ?? 'SHS DI MR',
        EQ_COUNTRY_ID: sys.EQ_COUNTRY_ID ?? 'DE',
      }));

      const components = Array.from({ length: componentCount }, (_, compIdx) => ({
        EQ_ID_HQ: `CMP${compIdx + 1}-HQ-${sys.EQ_ID_HQ ?? i}`,
        EQ_ID: `CMP${compIdx + 1}-CI-${sys.EQ_ID ?? i}`,
        EQ_MAT_ID: `MAT-CMP-${i + 1}-${compIdx + 1}`,
        EQ_SERIAL_ID: `SN-CMP-${i + 1}-${compIdx + 1}`,
        MAT_IVK_NAME: `Component ${compIdx + 1} IVK`,
        REL_SYS_MAT_IVK_NAME: `Component ${compIdx + 1} IVK`,
        EQ_TYPE: 'Component',
        EQ_STATUS: 'Active',
        EQ_SUB_STATUS: 'Active',
        LEVEL_BL_TEXT: sys.LEVEL_BL_TEXT ?? 'SHS DI MR',
        EQ_COUNTRY_ID: sys.EQ_COUNTRY_ID ?? 'DE',
      }));

      const licenses = Array.from({ length: licenseCount }, (_, licIdx) => ({
        EQ_ID_HQ: `LIC${licIdx + 1}-HQ-${sys.EQ_ID_HQ ?? i}`,
        EQ_ID: `LIC${licIdx + 1}-CI-${sys.EQ_ID ?? i}`,
        EQ_MAT_ID: `MAT-LIC-${i + 1}-${licIdx + 1}`,
        EQ_SERIAL_ID: `SN-LIC-${i + 1}-${licIdx + 1}`,
        MAT_IVK_NAME: `License ${licIdx + 1} IVK`,
        REL_SYS_MAT_IVK_NAME: `License ${licIdx + 1} IVK`,
        EQ_TYPE: 'License',
        EQ_STATUS: 'Active',
        EQ_SUB_STATUS: 'Active',
        LIC_CREATION_DT: `2024-0${(licIdx % 9) + 1}-01`,
        LIC_START_DT: `2024-0${(licIdx % 9) + 1}-01`,
        LIC_END_DT: `2026-0${(licIdx % 9) + 1}-01`,
        LEVEL_BL_TEXT: sys.LEVEL_BL_TEXT ?? 'SHS DI MR',
        EQ_COUNTRY_ID: sys.EQ_COUNTRY_ID ?? 'DE',
      }));

      return {
        ...sys,
        workstations,
        components,
        licenses,
        workstation: workstations[0],
        component: components[0],
        license: licenses[0],
      };
    });
  }
  // ---------------------------------------------------------------------------

  private fetchAllSystemsFromAPI(payload?: FilterPayload): void {
    if (!this.hasTestRunBeenExecuted || this.isTimerRunning) {
      return;
    }
    this.tableMessage = '';
    this.tableMessageType = '';
    this.loadingService.setLoading('eligibleSystemsTable', true);
    const filterToUse = payload ? payload : this.sharedService.latestPayload || { rootGroup: { type: 'Group', operator: 'AND', conditions: [] } };
    this.filteredChunkedResponse = [];
    this.chunkedResponse = [];
    this.columnSearchFilters = {};
    this.pageSize = 25;
    this.pageIndex = 1;
    this.clearTemporaryColumns();
    this.manuallyHiddenQueryColumns.clear();
    const selectedView = this.getSelectedResultView();
    if (selectedView) {
      this.allTableColumns.forEach(column => {
        column.visible = selectedView.columns.find(savedColumn => savedColumn.name === column.colName)?.visible ?? false;
      });
      this.applyResultViewColumns(selectedView.columns);
      this.isResultViewDirty = false;
      this.refreshColumnLists();
    }
    if (this.isFilterEmpty(filterToUse)) {
      this.allSystemsOriginal = [];
      this.totalRecords = 0;
      this.loadingService.setLoading('eligibleSystemsTable', false);
      return;
    }

    this.processPayloadForHiddenColumns(filterToUse);
    this.isTimerRunning = true;
    this.apiCallStartTime = performance.now();
    this.initiateTimer();
    this.showEligibleSystems = true;

    const useSharedMock = this.sharedService.useMatchedMockResponse && !!this.sharedService.mockedEligibleSystemsResponse;
    if (useSharedMock) {
      const data = this.sharedService.mockedEligibleSystemsResponse!;
      this.sharedService.useMatchedMockResponse = false;

      if (data.items && data.items.length !== 0) {
        const normalizedItems = this.normalizeIvkData(data.items);
        const preparedItems = this.MOCK_IVK_ENABLED
          ? this.applyMockIvkData(normalizedItems)
          : normalizedItems;

        this.chunkedResponse = this.chunkAndInitializeData(preparedItems);
        this.totalRecords = data.total;
        this.tableMessage = '';
        this.tableMessageType = '';
        this.fetchCountriesAvailableForFetching();
        this.computeIvkViews();
      } else {
        this.allSystemsOriginal = [];
        this.chunkedResponse = [];
        this.totalRecords = 0;
        this.tableMessage = "We couldn't find any records matching your search. Please refine your query and try again.";
        this.tableMessageType = 'info';
      }

      this.loadingService.setLoading('eligibleSystemsTable', false);
      this.isTimerRunning = false;
      this.cdr.detectChanges();
      return;
    }

    this.search.searchAllUpdateMachines(filterToUse).pipe(takeUntil(this.apiCancel$)).subscribe({
      next: (data) => {
        if (data && data.items && data.items.length != 0) {
          const normalizedItems = this.normalizeIvkData(data.items);
          const preparedItems = this.MOCK_IVK_ENABLED
            ? this.applyMockIvkData(normalizedItems)
            : normalizedItems;

          this.chunkedResponse = this.chunkAndInitializeData(preparedItems);
          this.totalRecords = data.total;
          this.tableMessage = '';
          this.tableMessageType = '';
          this.fetchCountriesAvailableForFetching();
          this.computeIvkViews();
        } else {
          this.allSystemsOriginal = [];
          this.chunkedResponse = [];
          this.totalRecords = 0;
          if (this.tableMessageType !== 'error') {
            this.tableMessage = "We couldn't find any records matching your search. Please refine your query and try again.";
            this.tableMessageType = 'info';
          }
        }
        this.loadingService.setLoading('eligibleSystemsTable', false);
        this.isTimerRunning = false;
        this.cdr.detectChanges();
      },
     error: (error) => {
        console.error('API error:', error);
        this.showEligibleSystems = true;
        this.allSystemsOriginal = [];
        this.totalRecords = 0;
        this.tableMessage = 'Request timed out due to a large result set or connectivity issue. Please narrow down your search criteria and try again.';
        this.tableMessageType = 'error';
        this.loadingService.setLoading('eligibleSystemsTable', false);
        this.isTimerRunning = false;
        this.cdr.detectChanges();
      },
    });
  }
  
  chunkAndInitializeData(data: EligibleSystem[]): EligibleSystem[][] {
    const chunkData = this.chunkArray(data, this.pageSize);
    this.allSystemsOriginal = chunkData[this.pageIndex];
    return chunkData;
  }

  private chunkArray(
    data: EligibleSystem[],
    chunkSize: number,
  ): EligibleSystem[][] { 
    const chunks: EligibleSystem[][] = [];
    let pageNumber = 1;
    for (let i = 0; i < data.length; i += chunkSize) {
      chunks[pageNumber] = data.slice(i, i + chunkSize);
      pageNumber++;
    }
    return chunks;
  }

  isShowAllSystems(): boolean {
    return this.pageSize === ALL_SYSTEMS;
  }

  fetchCountriesAvailableForFetching() {
    const allCountriesAvailable = [
      ...new Set(
        this.chunkedResponse
          .flat()
          .map(system => system['EQ_COUNTRY_ID'])
          .filter(country => country != null)
      )
    ];
      
     this.visibleTableColumns.map(col => {
      if (col.colName === 'EQ_COUNTRY_ID') {
        col.selectOptions = allCountriesAvailable.map(country => ({
          value: country,
          label: country
        }));
      }
    });
  }

  ngOnDestroy() {
    if (this.viewPopoverHideTimer) {
      clearTimeout(this.viewPopoverHideTimer);
      this.viewPopoverHideTimer = null;
    }
    this.clearScrollSyncListeners();
    this.destroy$.next();
    this.destroy$.complete();

    this.apiCancel$.next();
    this.apiCancel$.complete();
  }

   useVirtualScroll(): boolean {
    return this.isShowAllSystems() && this.filteredData.length > 50;
  }

  private initializeDynamicColumnFilters(): void {
    this.generatedIvkColumns.forEach((column) => {
      if (column.isFilterable && this.columnSearchFilters[column.colName] === undefined) {
        this.columnSearchFilters[column.colName] = '';
      }
    });
  }

  /**
   * Groups related records by EQ_ID_HQ so that records sharing the same
   * EQ_ID_HQ value are merged into a single slot (first-value-wins per field).
   * Records without an EQ_ID_HQ are each kept as their own separate slot.
   */
  private groupAndMergeByEqIdHq(records: RelatedIvkData[]): RelatedIvkData[] {
    const groups = new Map<string, RelatedIvkData>();
    const ungrouped: RelatedIvkData[] = [];

    for (const record of records) {
      const key = record['EQ_ID_HQ'];
      if (key == null) {
        ungrouped.push(record);
      } else if (groups.has(key)) {
        const existing = groups.get(key)!;
        for (const [field, value] of Object.entries(record)) {
          if (existing[field] == null && value != null) {
            existing[field] = value;
          }
        }
      } else {
        groups.set(key, { ...record });
      }
    }

    return [...groups.values(), ...ungrouped];
  }

  private getRelatedRecords(row: EligibleSystem, type: Exclude<IvkViewType, 'system'>): RelatedIvkData[] {
    const matchedKey = `MATCHED_${type.toUpperCase()}S` as keyof EligibleSystem;
    const pluralKey = `${type}s` as keyof EligibleSystem;
    const singularKey = type as keyof EligibleSystem;
    const matched = row[matchedKey];
    const plural = row[pluralKey];
    const singular = row[singularKey];

    if (Array.isArray(matched) && matched.length > 0) {
      return matched as RelatedIvkData[];
    }

    if (Array.isArray(plural) && plural.length > 0) {
      return plural as RelatedIvkData[];
    }

    return singular ? [singular as RelatedIvkData] : [];
  }

  private buildGeneratedIvkColumns(items: EligibleSystem[]): TableColumn[] {
    const columns: TableColumn[] = [];
    const baseOrder = this.defaultTableColumns.map((column) => String(column.colName));

    for (const section of this.relatedSectionConfig) {
      const maxCount = items.reduce(
        (max, item) => Math.max(max, this.groupAndMergeByEqIdHq(this.getRelatedRecords(item, section.type)).length),
        0
      );

      for (let index = 0; index < maxCount; index++) {
        const fieldNames = new Set<string>();

        items.forEach((item) => {
          const record = this.groupAndMergeByEqIdHq(this.getRelatedRecords(item, section.type))[index];
          if (!record) {
            return;
          }

          Object.entries(record).forEach(([field, value]) => {
            if (value === null || (!Array.isArray(value) && typeof value !== 'object')) {
              fieldNames.add(field);
            }
          });
        });

        const orderedFields = [
          ...baseOrder.filter((field) => fieldNames.has(field)),
          ...Array.from(fieldNames).filter((field) => !baseOrder.includes(field)).sort((left, right) => left.localeCompare(right)),
        ];

        orderedFields.forEach((field) => {
          const baseColumn = this.defaultTableColumns.find((column) => column.colName === field);

          columns.push({
            ...(baseColumn ?? {
              displayName: this.humanizeFieldName(field),
              visible: true,
              isFilterable: true,
              filterType: 'text',
            }),
            colName: this.buildDynamicColumnName(section.type, index, field),
            displayName: baseColumn?.displayName ?? this.humanizeFieldName(field),
            visible: true,
            isSortable: false,
            groupType: section.type,
            groupIndex: index,
            sourceField: field,
            isDynamic: true,
          });
        });
      }
    }

    return columns;
  }

  private flattenRelatedSection(row: EligibleSystem, records: RelatedIvkData[], type: Exclude<IvkViewType, 'system'>): void {
    records.forEach((record, index) => {
      Object.entries(record).forEach(([field, value]) => {
        if (value === null || (!Array.isArray(value) && typeof value !== 'object')) {
          row[this.buildDynamicColumnName(type, index, field)] = value ?? null;
        }
      });
    });
  }

  private buildDynamicColumnName(type: Exclude<IvkViewType, 'system'>, index: number, field: string): string {
    const section = this.relatedSectionConfig.find((config) => config.type === type);
    return `${section?.prefix ?? type.toUpperCase()}_${index + 1}_${field}`;
  }

  private humanizeFieldName(field: string): string {
    if (field === 'REL_SYS_EQ_ID') {
      return 'Related System EQ ID';
    }
    if (field === 'MAT_IVK_NAME' || field === 'REL_SYS_MAT_IVK_NAME') {
      return 'IVK Description';
    }
    return field
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ');
  }

  private getColumnGroupKey(column: TableColumn): string {
    if (column.groupType && column.groupIndex !== undefined) {
      return `${column.groupType}-${column.groupIndex}`;
    }

    return 'system';
  }

  private getColumnGroupLabel(column: TableColumn): string {
    if (!column.groupType || column.groupType === 'system' || column.groupIndex === undefined) {
      return 'System Data';
    }

    const section = this.relatedSectionConfig.find((config) => config.type === column.groupType);
    return `${section?.labelPrefix ?? column.groupType}-${column.groupIndex + 1}`;
  }

  private getIvkColumnSelector(view: IvkView): string {
    return `[data-ivk-group-key="${this.getIvkGroupKey(view)}"]`;
  }

  private getIvkGroupKey(view: IvkView): string {
    return view.type === 'system' ? 'system' : `${view.type}-${view.ivkIndex}`;
  }

  private getTableMode(isModal: boolean): TableMode {
    return isModal ? 'modal' : 'page';
  }

  private getTableHostElement(isModal: boolean): HTMLElement | null {
    return document.querySelector(`.eligible-systems-table-shell[data-table-mode="${this.getTableMode(isModal)}"]`);
  }

  private getTableScrollContainer(isModal: boolean, anchor?: HTMLElement | null): HTMLElement | null {
    const scrollableAncestor = this.findScrollableAncestor(anchor);
    if (scrollableAncestor) {
      return scrollableAncestor;
    }

    const host = this.getTableHostElement(isModal);
    if (!host) {
      return null;
    }

    const candidates = Array.from(
      host.querySelectorAll('.p-datatable-wrapper, .p-datatable-scrollable-body, .p-datatable-scrollable-header-box')
    ) as HTMLElement[];

    return candidates.find((candidate) => candidate.scrollWidth > candidate.clientWidth) ?? candidates[0] ?? null;
  }

  private findScrollableAncestor(element?: HTMLElement | null): HTMLElement | null {
    let current = element?.parentElement ?? null;

    while (current) {
      const computedStyle = window.getComputedStyle(current);
      const canScrollX = /(auto|scroll|overlay)/.test(computedStyle.overflowX) && current.scrollWidth > current.clientWidth;

      if (canScrollX) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private scheduleScrollSync(): void {
    setTimeout(() => {
      this.attachScrollSync(false);
      this.attachScrollSync(true);
    }, 0);
  }

  private attachScrollSync(isModal: boolean): void {
    const mode = this.getTableMode(isModal);
    const container = this.getTableScrollContainer(isModal);

    this.scrollSyncCleanup.get(mode)?.();
    this.scrollSyncCleanup.delete(mode);

    if (!container) {
      return;
    }

    const handler = () => {
      if (this.isSyncingProgrammaticScroll) {
        return;
      }

      this.syncActiveViewWithScroll(isModal);
    };

    container.addEventListener('scroll', handler, { passive: true });
    this.scrollSyncCleanup.set(mode, () => container.removeEventListener('scroll', handler));
    this.syncActiveViewWithScroll(isModal);
  }

  private syncActiveViewWithScroll(isModal: boolean): void {
    if (isModal !== this.isTableExpanded) {
      return;
    }

    const host = this.getTableHostElement(isModal);
    const container = this.getTableScrollContainer(isModal);

    if (!host || !container || this.ivkViews.length === 0) {
      return;
    }

    const containerLeft = container.getBoundingClientRect().left;
    let bestIndex = this.activeViewIdx;
    let bestDistance = Number.POSITIVE_INFINITY;

    this.ivkViews.forEach((view, index) => {
      const target = host.querySelector(this.getIvkColumnSelector(view)) as HTMLElement | null;
      if (!target) {
        return;
      }

      const distance = Math.abs(target.getBoundingClientRect().left - containerLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    if (bestIndex !== this.activeViewIdx) {
      this.activeViewIdx = bestIndex;
      this.cdr.detectChanges();
    }
  }

  private clearScrollSyncListeners(): void {
    this.scrollSyncCleanup.forEach((cleanup) => cleanup());
    this.scrollSyncCleanup.clear();
  }
}
